import { useMemo, useState } from 'react';
import { useGetMtoValidityQuery } from '@shared/api';
import type { QualityDocValidity } from '@entities/mto';
import {
  docTypeLabel,
  fmtDate,
  qualityDocStatusLabel,
  qualityDocStatusTone,
  todayIso,
  validityLabel,
  validityTone,
} from '@entities/mto';
import { Badge } from '@shared/ui/Badge';
import { Card } from '@shared/ui/Card';
import { Input, Label } from '@shared/ui/Input';
import {
  DeterministicNote,
  Empty,
  FilterBar,
  FilterField,
  Muted,
  SectionTitle,
  Table,
  TableScroll,
  Wrap,
} from '../styles';

export function ValidityTab() {
  const [workDate, setWorkDate] = useState<string>(todayIso());
  const dateFilter = workDate.trim();
  const { data, isLoading, isFetching } = useGetMtoValidityQuery(
    dateFilter ? { work_date: dateFilter } : undefined,
  );
  const items = useMemo<QualityDocValidity[]>(() => data?.items ?? [], [data]);

  return (
    <Wrap>
      <FilterBar>
        <FilterField>
          <Label htmlFor="qc-validity-date">Дата работ</Label>
          <Input
            id="qc-validity-date"
            type="date"
            value={workDate}
            onChange={(e) => setWorkDate(e.target.value)}
          />
        </FilterField>
      </FilterBar>
      <DeterministicNote>
        Действительность проверяется просто: срок действия документа сравнивается с датой работ, без
        искусственного интеллекта. Документ со сроком ровно на дату работ ещё считается
        действительным.
      </DeterministicNote>

      <Card>
        <SectionTitle>
          Документы на {fmtDate(data?.work_date ?? dateFilter)} <Badge $tone="neutral">{items.length}</Badge>
          {isFetching && !isLoading && <Muted as="span">обновление…</Muted>}
        </SectionTitle>
        {isLoading ? (
          <Muted>Загрузка отчёта…</Muted>
        ) : items.length === 0 ? (
          <Empty>Документов качества в системе пока нет.</Empty>
        ) : (
          <TableScroll>
            <Table>
              <thead>
                <tr>
                  <th scope="col">Тип</th>
                  <th scope="col">Номер</th>
                  <th scope="col">Орган</th>
                  <th scope="col">Действует до</th>
                  <th scope="col">Статус</th>
                  <th scope="col">Вердикт</th>
                </tr>
              </thead>
              <tbody>
                {items.map(({ doc, validity }) => (
                  <tr key={doc.id}>
                    <td>{docTypeLabel(doc.doc_type)}</td>
                    <td>{doc.number ?? '—'}</td>
                    <td>{doc.issuer ?? '—'}</td>
                    <td>{doc.valid_until ? fmtDate(doc.valid_until) : 'бессрочно'}</td>
                    <td>
                      <Badge $tone={qualityDocStatusTone(doc.status)}>
                        {qualityDocStatusLabel(doc.status)}
                      </Badge>
                    </td>
                    <td>
                      <Badge $tone={validityTone(validity)}>{validityLabel(validity)}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableScroll>
        )}
      </Card>
    </Wrap>
  );
}
